import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BedDouble, Building2, Hotel, Mail, Tag } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { setSeoMetadata } from '../lib/seo';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useHeroMedia } from '../hooks/useHeroMedia';
import { CONTACT_PAGE, ROUTES } from '../data/stjSite';

const VILLA_DIRECTORY = 'https://villas.reservestj.com';
const AGENT_REGISTER = 'https://reservestj.com/villa-pricing-packages';

/**
 * Content of reservestj.com/st-john-villa-rentals, kept where it can be edited without touching
 * the layout. The discounted list is exactly what the legacy page publishes - the dates are the
 * owner's to refresh (they are from the 2023 season).
 */
const DISCOUNTED_VILLAS = [
  { name: 'Studio Virgin Grand', price: '$1,495', dates: '10/14-10/21/2023' },
  { name: 'Two-Bedroom Bay Vista Villa', price: '$4,045', dates: '11/12-11/19/2023' },
  { name: 'Three-Bedroom Pool Villa', price: '$4,855', dates: '11/18-11/25/2023' },
  { name: 'Two-Bedroom Bay Vista Villa', price: '$4,315', dates: '11/19-11/26/2023' },
];

/**
 * /st-john-villa-rentals - the legacy Villa Rental Information page.
 *
 * Reached from the footer ISLAND DIRECTORY menu only; the top menu is untouched on purpose.
 * "concierge service" points at the site's own booking page rather than the old external
 * scheduler, matching the contact page.
 */
const VillaRentalsPage = () => {
  const { supportEmail } = useSiteSettings();
  const hero = useHeroMedia();
  const email = supportEmail || 'info@reservestj.com';

  useEffect(() => {
    const description = 'St John villa rentals - find the villa or vacation property you are looking for in '
      + 'one place, book direct or use our complimentary five-star concierge service.';
    setSeoMetadata({
      title: 'Villa Rental Information',
      description,
      keywords: 'st john villa rentals, st john vacation rentals, usvi villas, st john luxury villas',
      canonicalPath: ROUTES.villaRentals,
      ogType: 'website',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'St John Villa Rentals',
        description,
        about: { '@type': 'LodgingBusiness', name: 'St John Luxury Villas', areaServed: 'St John, U.S. Virgin Islands' },
      },
    });
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="villa-rentals-page">
      <Header />

      {/* ================= HERO ================= */}
      <section className="relative flex min-h-[46vh] items-center justify-center overflow-hidden lg:min-h-[54vh]" data-testid="villa-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-sky-100 via-white to-white" />
        {hero.videoUrl ? (
          <video key={hero.videoUrl} autoPlay muted loop playsInline preload="metadata"
            poster={hero.backgroundImageUrl || undefined}
            className="absolute inset-0 h-full w-full object-cover">
            <source src={hero.videoUrl} type={hero.videoMimeType} />
          </video>
        ) : hero.backgroundImageUrl ? (
          <div className="absolute inset-0 h-full w-full bg-cover bg-center" style={{ backgroundImage: `url(${hero.backgroundImageUrl})` }} />
        ) : null}
        <div className="absolute inset-0 bg-black/25" />
        <div className="relative z-10 w-full px-4 py-24 text-center">
          <h1 className="text-4xl font-extrabold italic tracking-tight text-white drop-shadow-[0_3px_10px_rgba(0,0,0,0.75)] sm:text-5xl lg:text-6xl">
            Villa Rental Information
          </h1>
          <h2 className="mt-4 text-xl font-semibold text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.75)] sm:text-2xl">
            St John, Virgin Islands
          </h2>
        </div>
      </section>

      {/* ================= INTRO ================= */}
      <section className="mx-auto max-w-5xl px-4 py-12 text-center lg:py-16">
        <h2 className="text-3xl font-extrabold uppercase italic leading-tight tracking-tight text-slate-900 sm:text-4xl" data-testid="villa-intro-heading">
          Find the Villa or Vacation Property
          <span className="block">you are looking for in one place</span>
        </h2>
        <p className="mt-5 text-lg text-slate-600">
          You can book direct or use our{' '}
          <Link to={CONTACT_PAGE.scheduleCall} className="font-semibold text-blue-800 underline hover:text-blue-900" data-testid="villa-concierge-link">
            concierge service
          </Link>.
        </p>
        <a
          href={VILLA_DIRECTORY}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-red-600 px-8 py-4 font-bold uppercase tracking-wide text-white shadow-lg transition hover:bg-red-700"
          data-testid="villa-directory-top"
        >
          <Hotel className="h-5 w-5" /> Visit Our New Island Villa Directory
        </a>
      </section>

      {/* ================= LUXURY VILLAS + DISCOUNTS ================= */}
      <section className="mx-auto max-w-7xl px-4 pb-14">
        <div className="grid items-start gap-10 lg:grid-cols-2">
          <div>
            <img
              src="/images/villas/1-luxury-villa-rentals-st-john-virgin-islands.jpg"
              alt="Luxury villa rentals on St John, U.S. Virgin Islands"
              className="w-full rounded-2xl object-cover shadow-lg"
              loading="lazy"
            />
            <h3 className="mt-6 text-2xl font-extrabold uppercase italic tracking-tight text-slate-900" data-testid="villa-luxury-heading">
              St John Luxury Villas
            </h3>
            <p className="mt-3 text-slate-600">
              Rent a Private Island Oasis that Redefines Luxury Accommodations. Exquisitely Isolated.
            </p>
            <p className="mt-3 text-slate-600">
              Many villas at your fingertips, Discover exclusive St John villa rentals with amenities to
              accommodate your every need. Ask about our complimentary five-star concierge service.
            </p>
          </div>

          <div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6" data-testid="villa-discounts">
              <h4 className="flex items-center gap-2 text-xl font-extrabold uppercase italic tracking-tight text-slate-900">
                <Tag className="h-5 w-5 text-red-600" /> Upcoming Discounted Villas:
              </h4>
              <ul className="mt-4 space-y-3">
                {DISCOUNTED_VILLAS.map((villa) => (
                  <li key={`${villa.name}-${villa.dates}`} className="flex items-start gap-3 border-b border-slate-200 pb-3 last:border-0 last:pb-0">
                    <BedDouble className="mt-0.5 h-4 w-4 shrink-0 text-blue-700" />
                    <span>
                      <span className="block font-semibold text-slate-800">{villa.name}: {villa.price}</span>
                      <span className="text-sm text-slate-500">{villa.dates}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <a
                href={VILLA_DIRECTORY}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-blue-800 px-6 py-3.5 font-bold uppercase tracking-wide text-white transition hover:bg-blue-900"
                data-testid="villa-directory-bottom"
              >
                <Hotel className="h-5 w-5" /> Visit Villa Directory
              </a>
            </div>

            <img
              src="/images/villas/st-john-condos.jpg"
              alt="St John condos"
              className="mt-6 w-full rounded-2xl object-cover shadow-md"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* ================= AGENTS ================= */}
      <section className="border-y border-slate-200 bg-slate-50 py-12" data-testid="villa-agents">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <h3 className="flex flex-wrap items-center justify-center gap-2 text-2xl font-extrabold uppercase italic tracking-tight text-slate-900">
            <Building2 className="h-6 w-6 text-blue-800" /> Are You An Agent? Do You Own or Manage Properties?
          </h3>
          <p className="mt-4 text-slate-600">
            If your Properties are in St John USVI, Simply Sign Up, Choose a package and start adding your
            properties...
          </p>
          <a
            href={AGENT_REGISTER}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-red-600 px-8 py-4 font-bold uppercase tracking-wide text-white shadow-lg transition hover:bg-red-700"
            data-testid="villa-register"
          >
            Register <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* ================= NEWSLETTER / MISSING SOMETHING ================= */}
      <section className="mx-auto max-w-4xl px-4 py-12 text-center" data-testid="villa-newsletter">
        <h3 className="text-2xl font-extrabold uppercase italic tracking-tight text-slate-900">Subscribe To Our Newsletter</h3>
        <p className="mt-4 text-slate-600">
          If you see something missing from our site, please reach out, send an email to{' '}
          <a href={`mailto:${email}`} className="font-semibold text-blue-800 underline hover:text-blue-900" data-testid="villa-email">
            {email}
          </a>{' '}
          and let us know what we can add, and we will do our very best to get it in place.
        </p>
        <a
          href={`mailto:${email}`}
          className="mt-6 inline-flex items-center gap-2 rounded-full border-2 border-blue-800 bg-white px-7 py-3.5 font-bold uppercase tracking-wide text-blue-800 transition hover:bg-blue-50"
        >
          <Mail className="h-5 w-5" /> Email Us
        </a>
      </section>

      <Footer />
    </div>
  );
};

export default VillaRentalsPage;
